import { createBrowserRouter, Navigate } from 'react-router-dom'
import Auth from '../pages/Auth'
import AuthGate from '../pages/Auth/components/AuthGate'
import DeviceMap from '../pages/DeviceMap'
import ContentLoadedGate from '../pages/Main/ContentLoadedGate'
import urls from './urls.ts'
import LiftPage from '../pages/Equipment/pages/LiftPage'
import MaintenancePage from '../pages/Processes/Maintenance'
import EquipmentModels from '../pages/Equipment/pages/EquipmentModels'
import ParametersPage from '@/pages/Parameters/index.tsx'
import OrganizationsPage from '@/pages/Organizations'
import OrganizationUsers from '@/pages/Organizations/pages/OrganizationUsers'
import MDMPage from '@/pages/MDM'
import EquipmentPage from '@/pages/Equipment'
import AlarmSituationsPage from '@/pages/Processes/AlarmSituations/index.tsx'
import SystemEventsPage from '@/pages/SystemEvents/index.tsx'
import Dashboards from '@/pages/Dashboards'
import DashboardScreen from '@/pages/Dashboards/pages/DashboardScreen'
import Library from '@/pages/Library'
import AIAgentPage from '@/pages/AIAgent'
import LiftMonitoring from '@/pages/Equipment/pages/LiftPage/pages/LiftMonitoring'
import CalendarPage from '@/pages/Calendar/index.tsx'
import MainPage from '@/pages/Main/MainPage/index.tsx'
import LiftAnalysis from '@/pages/Equipment/pages/LiftPage/pages/LiftAnalysis'
import MyOrganizationUsers from '@/pages/MyOrganization/pages/MyOrganizationUsers/index.tsx'
import EmployePage from '@/pages/MyOrganization/pages/EmployePage/index.tsx'

const router = createBrowserRouter([
  {
    path: urls.auth,
    element: <Auth />,
  },
  {
    path: urls.home,
    element: <Navigate to={urls.main} replace={true} />,
  },
  {
    path: urls.main,
    handle: {
      crumb: () => 'Главная',
      canNavigate: true,
    },
    element: (
      <AuthGate>
        <ContentLoadedGate>
          <MainPage />
        </ContentLoadedGate>
      </AuthGate>
    ),
  },
  {
    path: urls.map,
    element: (
      <AuthGate>
        <ContentLoadedGate>
          <DeviceMap />
        </ContentLoadedGate>
      </AuthGate>
    ),
    handle: {
      crumb: () => 'Карта',
      canNavigate: true,
    },
  },
  {
    path: urls.calendar,
    handle: {
      crumb: () => 'Календарь ТО',
      canNavigate: true,
    },
    element: (
      <AuthGate>
        <ContentLoadedGate>
          <CalendarPage />
        </ContentLoadedGate>
      </AuthGate>
    ),
  },
  {
    path: urls.dashboards,
    children: [
      {
        path: '/dashboards',
        element: (
          <AuthGate>
            <ContentLoadedGate>
              <Dashboards />
            </ContentLoadedGate>
          </AuthGate>
        ),
        handle: {
          crumb: () => 'Экраны',
          canNavigate: true,
        },
      },
      {
        path: '/dashboards/:id',
        element: (
          <AuthGate>
            <ContentLoadedGate>
              <DashboardScreen />
            </ContentLoadedGate>
          </AuthGate>
        ),
      },
    ],
    handle: {
      crumb: () => 'Дашборды',
      canNavigate: true,
    },
  },
  {
    path: urls.aiAssistant,
    handle: {
      crumb: () => 'AI ассистент',
      canNavigate: true,
    },
    element: (
      <AuthGate>
        <ContentLoadedGate>
          <AIAgentPage />
        </ContentLoadedGate>
      </AuthGate>
    ),
  },
  {
    path: urls.equipment,
    handle: {
      crumb: () => 'Оборудование',
      canNavigate: false,
    },
    children: [
      {
        path: `${urls.lifts}/:id`,
        handle: {
          crumb: () => 'Лифт',
          canNavigate: false,
        },
        element: (
          <AuthGate>
            <ContentLoadedGate>
              <LiftPage />
            </ContentLoadedGate>
          </AuthGate>
        ),
      },
      {
        path: `${urls.lifts}/:id/monitoring`,
        handle: {
          crumb: () => 'Мониторинг',
          canNavigate: false,
        },
        element: (
          <AuthGate>
            <ContentLoadedGate>
              <LiftMonitoring />
            </ContentLoadedGate>
          </AuthGate>
        ),
      },
      {
        path: `${urls.lifts}/:id/analysis`,
        handle: {
          crumb: () => 'Анализ',
          canNavigate: false,
        },
        element: (
          <AuthGate>
            <ContentLoadedGate>
              <LiftAnalysis />
            </ContentLoadedGate>
          </AuthGate>
        ),
      },
    ],
  },
  {
    path: urls.schemasAndData,
    handle: {
      crumb: () => 'Схемы и данные',
      canNavigate: false,
    },
    children: [
      {
        path: urls.mdm,
        handle: {
          crumb: () => 'MDM',
          canNavigate: true,
        },
        element: (
          <AuthGate>
            <ContentLoadedGate>
              <MDMPage />
            </ContentLoadedGate>
          </AuthGate>
        ),
      },
    ],
  },
  {
    path: urls.processes,
    handle: {
      crumb: () => 'Процессы',
      canNavigate: false,
    },
    children: [
      {
        path: urls.maintenance,
        handle: {
          crumb: () => 'Обслуживание',
          canNavigate: true,
        },
        element: (
          <AuthGate>
            <ContentLoadedGate>
              <MaintenancePage />
            </ContentLoadedGate>
          </AuthGate>
        ),
      },
      {
        path: urls.alarmSituations,
        handle: {
          crumb: () => 'Сигнальные ситуации',
          canNavigate: true,
        },
        element: (
          <AuthGate>
            <ContentLoadedGate>
              <AlarmSituationsPage />
            </ContentLoadedGate>
          </AuthGate>
        ),
      },
    ],
  },
  {
    path: urls.settings,
    handle: {
      crumb: () => 'Настройки',
      canNavigate: false,
    },
    children: [
      {
        path: urls.equipmentSettings,
        handle: {
          crumb: () => 'Устройства',
          canNavigate: true,
        },
        element: (
          <AuthGate>
            <ContentLoadedGate>
              <EquipmentPage />
            </ContentLoadedGate>
          </AuthGate>
        ),
      },
      {
        path: urls.equipmentModels,
        handle: {
          crumb: () => 'Модели оборудования',
          canNavigate: true,
        },
        element: (
          <AuthGate>
            <ContentLoadedGate>
              <EquipmentModels />
            </ContentLoadedGate>
          </AuthGate>
        ),
      },
      {
        path: urls.parameters,
        handle: {
          crumb: () => 'Переменные',
          canNavigate: true,
        },
        element: (
          <AuthGate>
            <ContentLoadedGate>
              <ParametersPage />
            </ContentLoadedGate>
          </AuthGate>
        ),
      },
      {
        path: urls.organizations,
        handle: {
          crumb: () => 'Организации',
          canNavigate: true,
        },
        element: (
          <AuthGate>
            <ContentLoadedGate>
              <OrganizationsPage />
            </ContentLoadedGate>
          </AuthGate>
        ),
      },
      {
        path: `${urls.organizations}/:id/users`,
        handle: {
          canNavigate: true,
          navTo: urls.organizations,
          crumb: () => 'Организации',
        },
        element: (
          <AuthGate>
            <ContentLoadedGate>
              <OrganizationUsers />
            </ContentLoadedGate>
          </AuthGate>
        ),
      },
      {
        path: urls.documentsLibrary,
        handle: {
          canNavigate: true,
          crumb: () => 'Бибилиотека документов',
        },
        element: (
          <AuthGate>
            <ContentLoadedGate>
              <Library />
            </ContentLoadedGate>
          </AuthGate>
        ),
      },
    ],
  },
  {
    path: urls.archives,
    handle: {
      crumb: () => 'Архивы',
      canNavigate: false,
    },
    children: [
      {
        path: urls.systemEvents,
        handle: {
          crumb: () => 'События системы',
          canNavigate: true,
        },
        element: (
          <AuthGate>
            <ContentLoadedGate>
              <SystemEventsPage />
            </ContentLoadedGate>
          </AuthGate>
        ),
      },
    ],
  },

  {
    path: urls.myOrganization,
    handle: {
      crumb: () => 'Организация',
      canNavigate: true,
    },
    children: [
      {
        path: urls.myOrganization,
        element: (
          <AuthGate>
            <ContentLoadedGate>
              <MyOrganizationUsers />
            </ContentLoadedGate>
          </AuthGate>
        ),
      },
      {
        path: `${urls.myOrganization}/:id`,
        handle: {
          crumb: () => 'Пользователь',
          canNavigate: false,
        },
        element: (
          <AuthGate>
            <ContentLoadedGate>
              <EmployePage />
            </ContentLoadedGate>
          </AuthGate>
        ),
      },
    ],
  },
])

export default router
